import { PlanMealRootItemType } from "../model/nutritionPlanTypes";
import {
  NutritionDailyType,
  NutritionWeeklyType,
} from "../model/nutritionPlanTypes";
import { ResolvedMealItem, resolveMealItem } from "../helpers/planSelectors";
import { mondayOf } from "../helpers/planDates";

/**
 * State for the nutrition plan view. Weeks are cached per Monday so moving
 * back and forth never refetches, and the provider never fetches the same
 * week twice at once — the nutrition API is rate limited. `empty` is a 404:
 * the subject has a plan, but not for that week (e.g. before the plan
 * started).
 */

import { LoadStatus } from "../../shared/loadStatus";

export type { LoadStatus };

export type DetailsSelection = {
  date: string;
  mealType: number;
  rootItemId: string;
  resolved: ResolvedMealItem;
};

export type IntakeSelection = {
  date: string;
  initialMealType: number;
};

export type NutritionPlanState = {
  /** Monday (yyyy-MM-dd) of the week shown. */
  weekStartDate: string;
  weeklyCache: Record<string, NutritionWeeklyType>;
  weeklyStatus: Record<string, LoadStatus>;
  details: DetailsSelection | null;
  intake: IntakeSelection | null;
};

export type NutritionPlanAction =
  | { type: "GO_TO_WEEK"; monday: string }
  | { type: "WEEKLY_LOADING"; monday: string }
  | { type: "WEEKLY_LOADED"; monday: string; plan: NutritionWeeklyType | null }
  | { type: "WEEKLY_FAILED"; monday: string }
  | {
      type: "OPEN_DETAILS";
      date: string;
      mealType: number;
      rootItem: PlanMealRootItemType;
    }
  | { type: "CLOSE_DETAILS" }
  | { type: "OPEN_INTAKE"; date: string; mealType: number }
  | { type: "CLOSE_INTAKE" };

export const createInitialState = (today: string): NutritionPlanState => ({
  weekStartDate: mondayOf(today),
  weeklyCache: {},
  weeklyStatus: {},
  details: null,
  intake: null,
});

/** The day for `date` from its cached week, if that week has loaded. */
export const selectDay = (
  state: NutritionPlanState,
  date: string
): NutritionDailyType | undefined =>
  state.weeklyCache[mondayOf(date)]?.daily?.find((day) => day.date === date);

export const nutritionPlanReducer = (
  state: NutritionPlanState,
  action: NutritionPlanAction
): NutritionPlanState => {
  switch (action.type) {
    case "GO_TO_WEEK":
      return { ...state, weekStartDate: action.monday };

    case "WEEKLY_LOADING":
      return {
        ...state,
        weeklyStatus: { ...state.weeklyStatus, [action.monday]: "loading" },
      };

    case "WEEKLY_LOADED":
      if (!action.plan) {
        return {
          ...state,
          weeklyStatus: { ...state.weeklyStatus, [action.monday]: "empty" },
        };
      }
      return {
        ...state,
        weeklyCache: { ...state.weeklyCache, [action.monday]: action.plan },
        weeklyStatus: { ...state.weeklyStatus, [action.monday]: "ready" },
      };

    case "WEEKLY_FAILED":
      return {
        ...state,
        weeklyStatus: { ...state.weeklyStatus, [action.monday]: "error" },
      };

    case "OPEN_DETAILS":
      return {
        ...state,
        intake: null,
        details: {
          date: action.date,
          mealType: action.mealType,
          rootItemId: action.rootItem.id,
          resolved: resolveMealItem(action.rootItem),
        },
      };

    case "CLOSE_DETAILS":
      return state.details ? { ...state, details: null } : state;

    case "OPEN_INTAKE":
      return {
        ...state,
        details: null,
        intake: { date: action.date, initialMealType: action.mealType },
      };

    case "CLOSE_INTAKE":
      return state.intake ? { ...state, intake: null } : state;

    default:
      return state;
  }
};
